import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipRenderProps } from 'react-joyride';
import { useNavigate } from 'react-router-dom';

import { Button } from 'antd';
import { multiPageTourSteps } from 'data/tourSteps';

import {
  selectGuideCompletedPageTours,
  selectIsApplicationsGuideCompleted,
  selectIsShowGuide
} from 'features/auth/authSelectors';
import { closeSidebar } from 'features/sidebar/sidebarSlice';
import { selectTourState } from 'features/tour/tourSelectors';
import { setIsOpenApplicationTour, stopTour } from 'features/tour/tourSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { dismissGuide } from 'utils/tour/dismissGuide';

import { APPLICATIONS } from 'constants/path';
import { APPLICATIONS_MANAGEMENT_TOUR_DELAY_MS } from 'constants/tour';

import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left-long.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right-long.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

import ActionButton from '../ActionButton/ActionButton';
import styles from './CustomTooltip.module.scss';

const CustomTooltip = ({
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps
}: TooltipRenderProps) => {
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useAppSelector(selectTourState);
  const isApplicationsGuideCompleted = useAppSelector(
    selectIsApplicationsGuideCompleted
  );
  const isShowGuideSkipButton = useAppSelector(selectIsShowGuide);
  const guideCompletedPageTours = useAppSelector(selectGuideCompletedPageTours);

  const currentStep = state.steps[index];
  const isLastStep = index === state.steps.length - 1;
  const isNotFirstTourStep = index > 0;
  const isFullTour = useMemo(() => {
    return state.steps.length === multiPageTourSteps.length;
  }, [state.steps]);
  const shouldShowApplicationsButton =
    currentStep?.shouldShowApplicationsButton && isFullTour;

  const handleApplicationsTourStart = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const isFullTourLastJobsStep = isFullTour && shouldShowApplicationsButton;

    if (isFullTourLastJobsStep) {
      primaryProps?.onClick?.(event);
      return;
    }

    dispatch(stopTour());
    dispatch(closeSidebar());
    dispatch(setIsOpenApplicationTour(true));

    setTimeout(() => {
      navigate(APPLICATIONS);
    }, APPLICATIONS_MANAGEMENT_TOUR_DELAY_MS);
  };

  const handleCloseTour = () => {
    dispatch(stopTour());
  };

  const handleSkipPermanently = async () => {
    await dismissGuide({
      guideCompleted: guideCompletedPageTours,
      dispatch,
      catchError,
      errorMessage: t('error.message.finishTourFailed'),
      onClose: handleCloseTour
    });
  };

  return (
    <div {...tooltipProps} className={styles.container}>
      <Button {...closeProps} title="" className={styles.closeButton}>
        <CloseIcon />
      </Button>

      {step.content}

      <div className={styles.buttonsContainer}>
        {shouldShowApplicationsButton && (
          <ActionButton
            title={t('tour.button.applicationsManagement')}
            onClick={handleApplicationsTourStart}
            isCompleted={isApplicationsGuideCompleted}
          />
        )}

        <div className={styles.buttonsWrapperContainer}>
          <div className={styles.buttonsWrapper}>
            <Button
              {...skipProps}
              type="link"
              className={styles.button}
              title=""
            >
              {t('tour.button.skip')}
            </Button>

            {isNotFirstTourStep && (
              <Button
                {...backProps}
                className={styles.button}
                title=""
                icon={<ArrowLeftIcon />}
              >
                {t('tour.button.previous')}
              </Button>
            )}

            <Button
              {...primaryProps}
              type="primary"
              className={styles.button}
              icon={<ArrowRightIcon />}
              iconPosition="end"
              title=""
            >
              {isLastStep ? t('tour.button.finish') : t('tour.button.next')}
            </Button>
          </div>
          {isShowGuideSkipButton && (
            <Button
              className={styles.btnRed}
              type="link"
              onClick={handleSkipPermanently}
            >
              {t('guideModal.skipPermanently')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomTooltip;
